import api from "@/lib/api";

let companyCache = null;
let companyCacheAt = 0;
const CACHE_MS = 60_000;

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Load company (+ branches) for report letterheads. Cached briefly.
 */
export async function fetchReportCompany(force = false) {
  const now = Date.now();
  if (!force && companyCache && now - companyCacheAt < CACHE_MS) {
    return companyCache;
  }
  try {
    const { data } = await api.get("/company");
    companyCache = data.company || null;
    companyCacheAt = now;
    return companyCache;
  } catch {
    return companyCache;
  }
}

/**
 * Resolve letterhead fields from company settings, optionally overriding
 * contact lines with the selected branch when branch has its own phone/address.
 */
export async function resolveReportLetterhead({ branchId, allBranches } = {}) {
  const company = await fetchReportCompany();
  if (!company) {
    return {
      name: "Restaurant",
      legal_name: "",
      address: "",
      phone: "",
      email: "",
      tax_number: "",
      trade_license: "",
      branch_name: allBranches ? "All branches" : "",
      currency: "AED",
    };
  }

  const branches = company.branches || [];

  if (allBranches) {
    const addressParts = [company.address, company.city].filter(Boolean);
    return {
      name: company.name || "Restaurant",
      legal_name: company.legal_name || "",
      address: addressParts.join(", "),
      phone: company.phone || "",
      email: company.email || "",
      tax_number: company.tax_number || "",
      trade_license: company.trade_license || "",
      branch_name: "All branches",
      branch_code: "",
      currency: company.currency || "AED",
    };
  }

  const branch =
    (branchId && branches.find((b) => Number(b.id) === Number(branchId))) ||
    branches.find((b) => b.is_active) ||
    branches[0] ||
    null;

  const addressParts = [
    branch?.address || company.address,
    branch?.mall_name,
    branch?.city,
  ].filter(Boolean);

  return {
    name: company.name || "Restaurant",
    legal_name: company.legal_name || "",
    address: addressParts.join(", "),
    phone: branch?.phone || company.phone || "",
    email: company.email || "",
    tax_number: company.tax_number || "",
    trade_license: company.trade_license || "",
    branch_name: branch?.brand_name || branch?.name || "",
    branch_code: branch?.code || "",
    currency: company.currency || "AED",
  };
}

export function reportLetterheadLines(company) {
  const lines = [];
  if (company?.name) lines.push(company.name);
  if (company?.legal_name && company.legal_name !== company.name) {
    lines.push(company.legal_name);
  }
  if (company?.branch_name) {
    lines.push(
      company.branch_code
        ? `${company.branch_name} · ${company.branch_code}`
        : company.branch_name,
    );
  }
  if (company?.address) lines.push(company.address);
  const contact = [];
  if (company?.phone) contact.push(`Tel: ${company.phone}`);
  if (company?.email) contact.push(`Email: ${company.email}`);
  if (contact.length) lines.push(contact.join(" · "));
  const reg = [];
  if (company?.tax_number) reg.push(`TRN: ${company.tax_number}`);
  if (company?.trade_license) reg.push(`Trade Licence: ${company.trade_license}`);
  if (reg.length) lines.push(reg.join(" · "));
  return lines;
}

function letterheadHtml(company) {
  if (!company) return "";

  const lines = [];
  if (company.legal_name && company.legal_name !== company.name) {
    lines.push(`<div class="lh-sub">${esc(company.legal_name)}</div>`);
  }
  if (company.branch_name) {
    lines.push(`<div class="lh-branch">${esc(company.branch_name)}${company.branch_code ? ` · ${esc(company.branch_code)}` : ""}</div>`);
  }
  if (company.address) {
    lines.push(`<div class="lh-line">${esc(company.address)}</div>`);
  }

  const contactBits = [];
  if (company.phone) contactBits.push(`Tel: ${esc(company.phone)}`);
  if (company.email) contactBits.push(`Email: ${esc(company.email)}`);
  if (contactBits.length) {
    lines.push(`<div class="lh-line">${contactBits.join(" · ")}</div>`);
  }

  const regBits = [];
  if (company.tax_number) regBits.push(`TRN: ${esc(company.tax_number)}`);
  if (company.trade_license) regBits.push(`Trade Licence: ${esc(company.trade_license)}`);
  if (regBits.length) {
    lines.push(`<div class="lh-reg">${regBits.join(" · ")}</div>`);
  }

  return `
  <header class="letterhead">
    <div class="lh-name">${esc(company.name || "Restaurant")}</div>
    ${lines.join("")}
  </header>`;
}

const PRINT_CSS = `
  @page { margin: 12mm 12mm 14mm; }
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", Tahoma, Arial, sans-serif;
    font-size: 11px;
    color: #1f140f;
    margin: 0;
    padding: 0;
  }
  .letterhead {
    text-align: center;
    border-bottom: 2px solid #F36F21;
    padding: 0 0 10px;
    margin: 0 0 14px;
  }
  .lh-name {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #2B0B04;
    margin: 0 0 2px;
  }
  .lh-sub {
    font-size: 11px;
    color: #6B3A2E;
    font-weight: 600;
  }
  .lh-branch {
    font-size: 11px;
    color: #F36F21;
    font-weight: 700;
    margin-top: 2px;
  }
  .lh-line, .lh-reg {
    font-size: 10px;
    color: #5c463c;
    line-height: 1.45;
    margin-top: 2px;
  }
  .lh-reg { font-weight: 600; color: #2B0B04; }
  .doc-title {
    font-size: 15px;
    font-weight: 800;
    margin: 0 0 6px;
    color: #2B0B04;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    justify-content: center;
    margin: 0 0 14px;
    padding: 8px 10px;
    background: #FFF8F3;
    border: 1px solid #F0E0D4;
    border-radius: 6px;
    color: #4A1810;
    font-size: 10.5px;
    line-height: 1.45;
  }
  .meta strong { color: #2B0B04; }
  h2 {
    font-size: 12px;
    margin: 14px 0 6px;
    color: #2B0B04;
    border-bottom: 1px solid #F0E0D4;
    padding-bottom: 3px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
    font-size: 10.5px;
  }
  th, td {
    border: 1px solid #e5d5c8;
    padding: 5px 6px;
    vertical-align: top;
  }
  th {
    background: #FFF3E8;
    color: #4A1810;
    text-align: left;
    font-weight: 700;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  tbody tr:nth-child(even) td { background: #FFFCFA; }
  .num, .mono { font-family: Consolas, "Courier New", monospace; }
  .num { text-align: right; white-space: nowrap; }
  tfoot th, tfoot td {
    background: #FFF3E8;
    font-weight: 700;
  }
  .footer {
    margin-top: 18px;
    padding-top: 8px;
    border-top: 1px solid #F0E0D4;
    font-size: 9px;
    color: #8B6B5E;
    text-align: center;
  }
  @media print {
    .letterhead { break-after: avoid; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; }
  }
`;

/**
 * Build a full printable HTML document with restaurant letterhead.
 */
export function buildReportDocument({
  title,
  company,
  metaHtml = "",
  bodyHtml = "",
  printedAt = new Date(),
}) {
  const when = printedAt.toLocaleString();
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>${PRINT_CSS}</style></head><body>
  ${letterheadHtml(company)}
  <div class="doc-title">${esc(title)}</div>
  ${metaHtml ? `<div class="meta">${metaHtml}</div>` : ""}
  ${bodyHtml}
  <div class="footer">Printed ${esc(when)} · ${esc(company?.name || "Restaurant ERP")}</div>
</body></html>`;
}

export function openReportPrint(html) {
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return false;
  }
  doc.open();
  doc.write(html);
  doc.close();
  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();
  setTimeout(() => {
    if (iframe.parentNode) document.body.removeChild(iframe);
  }, 1200);
  return true;
}

/** Convenience: period line used by many sales-style reports. */
export function periodMeta(report) {
  const bits = [];
  if (report?.from_date) {
    bits.push(`<div><strong>Period:</strong> ${esc(report.from_date)} to ${esc(report.to_date || report.from_date)}</div>`);
  } else if (report?.date) {
    bits.push(`<div><strong>Date:</strong> ${esc(report.date)}</div>`);
  }
  const pay = report?.payment_method;
  if (pay && pay !== "all") {
    const labels = {
      cash: "Cash",
      card: "Card",
      credit: "Credit",
      mixed: "Split / mixed",
    };
    bits.push(`<div><strong>Payment:</strong> ${esc(labels[pay] || pay)}</div>`);
  }
  return bits.join("");
}

export { esc as escapeReportHtml };
